/**
 * Data-grounded spec FAQ builder — FAQ rewrite §5
 * (docs/Page-Types-And-Templates.md). Replaces the retired templated
 * ProductFAQ (lib/faq/legacy-spec-faq-templates.ts).
 *
 * Contract (§2, non-negotiable):
 *   - Real-or-Hidden: a question renders ONLY when its data condition passes
 *     (normalized + plausibility-bounded). No 'N/A', no filler, no hedges.
 *   - No fabricated derivations; estimates are labeled as estimates.
 *   - No prices, no retailer names, no self-promotion in answers.
 *   - Sentence 1 answers the question with the number (snippet-quotable).
 *   - Max 6 questions: fit, capacity, energy, noise + up to 2 type-specific.
 *
 * Output feeds BOTH the visible component (spec-faq.tsx) and the FAQPage
 * JSON-LD (page.tsx) — schema equals visible content by construction.
 * `answer` is plain text; an optional trailing tool link is carried
 * structurally in `toolLink` and folded into `schemaAnswer` as plain text.
 */
import { normalizeDimensions, hasReliableDimensions } from '@/lib/dimension-utils'
import {
  applianceFamily,
  plausibleDims,
  plausibleCapacity,
  plausibleNoiseDb,
  plausibleEnergyKwhYr,
  type ApplianceFamily,
} from '@/lib/faq/spec-plausibility'

export interface SpecFaqToolLink {
  /** plain-text sentence prefix, e.g. "Estimate your exact cost with the " */
  prefix: string
  /** anchor text, e.g. "energy cost calculator" */
  text: string
  href: string
}

export interface SpecFaq {
  slot: string
  question: string
  /** plain text, no HTML */
  answer: string
  toolLink?: SpecFaqToolLink
  /** answer + tool-link sentence flattened to plain text (for JSON-LD) */
  schemaAnswer: string
}

/**
 * Minimum spec-FAQ items for the section to render. Shared by the visible
 * component (spec-faq.tsx) AND the page.tsx schema feed so schema==visible
 * can never diverge: a single orphan question must appear in neither.
 */
export const SPEC_FAQ_MIN_RENDER = 2

export interface SpecFaqInputs {
  brand?: string | null
  model?: string | null
  title?: string | null
  type?: string | null
  capacity_cuft?: number | null
  width_in?: number | null
  height_in?: number | null
  depth_in?: number | null
  noise_db?: number | null
  specs_json?: Record<string, unknown> | null
  features?: {
    hasIceMaker?: boolean
    hasSmartFeatures?: boolean
  }
  energy?: {
    kwhPerYear?: number | null
    annualCostNational?: number | null
    pctBelowFederal?: number | null
    isEnergyStar?: boolean
  }
}

// ── name handling ────────────────────────────────────────────────────────────
// Brand policy: ASIN/SKU-shaped "models" must never appear in question text
// (the legacy FAQ printed "Arctic Chef B00IR8H55A" — a policy violation).
const ASIN_LIKE = /\bB0[A-Z0-9]{8}\b/i
const JUNK_MODEL = /[_/]|^\d{3,}$|[a-f0-9]{12,}/i

const FAMILY_NOUN: Record<Exclude<ApplianceFamily, 'other'>, string> = {
  'fridge-fullsize': 'refrigerator',
  compact: 'mini fridge',
  'chest-freezer': 'chest freezer',
  'upright-freezer': 'upright freezer',
  'wine-beverage': 'wine & beverage cooler',
  kegerator: 'kegerator',
  'ice-maker': 'ice maker',
}

function typeNoun(family: Exclude<ApplianceFamily, 'other'>, type: string | null | undefined): string {
  const t = (type || '').toLowerCase()
  if (family === 'fridge-fullsize') {
    if (t.includes('french')) return 'French-door refrigerator'
    if (t.includes('side-by-side') || t.includes('side by side')) return 'side-by-side refrigerator'
    if (t.includes('top-freezer') || t.includes('top freezer')) return 'top-freezer refrigerator'
    if (t.includes('bottom-freezer') || t.includes('bottom freezer')) return 'bottom-freezer refrigerator'
    if (t.includes('counter-depth') || t.includes('counter depth')) return 'counter-depth refrigerator'
    return 'refrigerator'
  }
  if (family === 'wine-beverage') {
    if (t.includes('beverage')) return 'beverage cooler'
    return 'wine cooler'
  }
  return FAMILY_NOUN[family]
}

/**
 * Product reference WITH its article baked in, so templates can compose
 * grammatically for both forms: "the LG LRFLC2706S" (clean manufacturer
 * model) and "this LG French-door refrigerator" (ASIN/junk model fallback).
 * `ref` is mid-sentence casing, `Ref` is sentence-initial.
 */
interface NameRef {
  ref: string
  Ref: string
}

function displayName(inputs: SpecFaqInputs, family: Exclude<ApplianceFamily, 'other'>): NameRef {
  const brand = (inputs.brand || '').trim()
  const model = (inputs.model || '').trim()
  const cleanModel =
    model.length >= 3 &&
    model.length <= 16 &&
    !ASIN_LIKE.test(model) &&
    !JUNK_MODEL.test(model) &&
    /\d/.test(model)
  if (brand && cleanModel) {
    return { ref: `the ${brand} ${model}`, Ref: `The ${brand} ${model}` }
  }
  const noun = typeNoun(family, inputs.type)
  const base = brand ? `${brand} ${noun}` : noun
  return { ref: `this ${base}`, Ref: `This ${base}` }
}

// ── formatting helpers ───────────────────────────────────────────────────────
function fmtIn(n: number): string {
  const r = Math.round(n * 10) / 10
  return Number.isInteger(r) ? String(r) : r.toFixed(1)
}

function fmtCap(n: number): string {
  const r = Math.round(n * 10) / 10
  return Number.isInteger(r) ? String(r) : r.toFixed(1)
}

function withSchemaAnswer(f: Omit<SpecFaq, 'schemaAnswer'>): SpecFaq {
  const schemaAnswer = f.toolLink ? `${f.answer} ${f.toolLink.prefix}${f.toolLink.text}.` : f.answer
  return { ...f, schemaAnswer }
}

function specNum(specs: Record<string, unknown> | null | undefined, keys: string[]): number | null {
  for (const k of keys) {
    const v = specs?.[k]
    if (typeof v === 'number' && v > 0) return v
    if (typeof v === 'string' && /^\d+(\.\d+)?$/.test(v.trim())) return parseFloat(v)
  }
  return null
}

function specFlag(specs: Record<string, unknown> | null | undefined, keys: string[]): boolean {
  return keys.some((k) => {
    const v = specs?.[k]
    return v === true || v === 'true' || v === 'yes' || v === 1
  })
}

// ── slot builders ────────────────────────────────────────────────────────────
function fitFaq(
  inputs: SpecFaqInputs,
  family: Exclude<ApplianceFamily, 'other'>,
  name: NameRef
): SpecFaq | null {
  const raw = { width_in: inputs.width_in ?? null, height_in: inputs.height_in ?? null, depth_in: inputs.depth_in ?? null }
  if (!hasReliableDimensions(raw, inputs.type ?? undefined)) return null
  const norm = normalizeDimensions(raw, inputs.type ?? undefined)
  const dims = plausibleDims(family, norm.widthInches, norm.heightInches, norm.depthInches)
  if (!dims.ok || dims.width == null || dims.height == null) return null

  const w = dims.width
  const h = dims.height
  const d = dims.depth

  const clearance =
    family === 'chest-freezer'
      ? 'Leave room above for the lid to open fully and 2–3 inches behind for ventilation.'
      : family === 'fridge-fullsize' &&
          ((inputs.type || '').includes('french') || (inputs.type || '').includes('side-by-side'))
        ? `Each door needs about ${fmtIn(w / 2 + 2)} inches of swing clearance, plus 1–2 inches behind for ventilation.`
        : 'Allow 1–2 inches behind for ventilation and check the door-swing side against your layout.'

  const flushNote =
    (inputs.type || '').includes('counter-depth') && d != null
      ? ` At ${fmtIn(d)} inches deep it sits roughly flush with standard 24–25 inch counters.`
      : ''

  const isFridgeish = family === 'fridge-fullsize' || family === 'compact'

  return withSchemaAnswer({
    slot: 'fit',
    question: `Will ${name.ref} fit my space — what are its dimensions?`,
    answer:
      `${name.Ref} measures ${fmtIn(w)} inches wide × ${fmtIn(h)} inches tall${d != null ? ` × ${fmtIn(d)} inches deep` : ''}. ` +
      `Plan an opening at least ${fmtIn(w + 1)} inches wide and ${fmtIn(h + 1)} inches tall. ${clearance}${flushNote}`,
    toolLink: isFridgeish
      ? {
          prefix: 'Double-check your doorway and cutout with the ',
          text: 'refrigerator size calculator',
          href: '/tools/refrigerator-size-calculator',
        }
      : undefined,
  })
}

function capacityFaq(
  inputs: SpecFaqInputs,
  family: Exclude<ApplianceFamily, 'other'>,
  name: NameRef
): SpecFaq | null {
  if (family === 'kegerator' || family === 'ice-maker') return null
  const cap = plausibleCapacity(family, inputs.capacity_cuft)
  if (cap == null) return null

  const freshCap = specNum(inputs.specs_json, ['fresh_food_capacity_cuft', 'refrigerator_capacity_cuft', 'fridge_capacity_cuft'])
  const fzCap = specNum(inputs.specs_json, ['freezer_capacity_cuft'])
  const split =
    freshCap != null && fzCap != null ? ` (${fmtCap(freshCap)} cu ft fresh / ${fmtCap(fzCap)} cu ft frozen)` : ''

  const isFreezer = family === 'chest-freezer' || family === 'upright-freezer'
  const sizing = isFreezer
    ? `As a common industry rule of thumb, each cubic foot holds roughly 35 lbs of frozen food, so figure on about ${Math.round(cap * 35)} lbs.`
    : cap < 10
      ? `That's about ${Math.max(1, Math.round(cap))} bags of groceries — suited to one or two people, a dorm, an office, or a secondary fridge.`
      : cap < 18
        ? `That's roughly ${Math.round(cap)} bags of groceries — comfortable for most smaller households.`
        : cap < 25
          ? `That's roughly ${Math.round(cap)} bags of groceries — enough for a family of four's weekly shop.`
          : `That's roughly ${Math.round(cap)} bags of groceries — generous for large families and entertainers.`

  return withSchemaAnswer({
    slot: 'capacity',
    question: `How much can ${name.ref} hold?`,
    answer: `${name.Ref} offers ${fmtCap(cap)} cubic feet of storage${split}. ${sizing}`,
  })
}

function energyFaq(inputs: SpecFaqInputs, name: NameRef): SpecFaq | null {
  const kwh = plausibleEnergyKwhYr(inputs.energy?.kwhPerYear)
  if (kwh == null) return null
  const cost = inputs.energy?.annualCostNational
  const pct = inputs.energy?.pctBelowFederal
  const star = inputs.energy?.isEnergyStar === true

  const costPart = typeof cost === 'number' && cost > 0 ? ` — roughly $${Math.round(cost)} per year at the average U.S. electricity rate` : ''
  const pctPart = typeof pct === 'number' && pct > 0 ? `, ${Math.round(pct)}% less than the federal standard for its size` : ''
  const starPart = star ? ' It is ENERGY STAR certified.' : ''

  return withSchemaAnswer({
    slot: 'energy',
    question: `How much does ${name.ref} cost to run per year?`,
    answer: `${name.Ref} uses about ${Math.round(kwh)} kWh per year${costPart}${pctPart}.${starPart}`,
    toolLink: {
      prefix: 'Rates vary by state — estimate your exact cost with the ',
      text: 'energy cost calculator',
      href: '/tools/energy-cost-calculator',
    },
  })
}

function noiseFaq(
  inputs: SpecFaqInputs,
  family: Exclude<ApplianceFamily, 'other'>,
  name: NameRef
): SpecFaq | null {
  const db = plausibleNoiseDb(inputs.noise_db)
  if (db == null) return null
  const anchor =
    db <= 40
      ? 'about as quiet as a library'
      : db <= 48
        ? 'comparable to a quiet office'
        : db <= 55
          ? 'about the level of a normal conversation'
          : 'audible in a quiet room, worth noting for open-plan spaces'
  const placement =
    family === 'compact'
      ? ' That matters most if it will run near a bed or desk.'
      : family === 'wine-beverage' || family === 'kegerator'
        ? ' That makes it easy to place in a living room, den, or home bar.'
        : ''
  return withSchemaAnswer({
    slot: 'noise',
    question: `How loud is ${name.ref}?`,
    answer: `${name.Ref} runs at about ${Math.round(db)} dB — ${anchor}.${placement}`,
  })
}

// ── type-specific slots (positive structured signal only) ────────────────────
function typeSpecificFaqs(
  inputs: SpecFaqInputs,
  family: Exclude<ApplianceFamily, 'other'>,
  name: NameRef,
  fitOk: boolean,
  normHeight: number | null
): SpecFaq[] {
  const out: SpecFaq[] = []
  const specs = inputs.specs_json
  const title = (inputs.title || '').toLowerCase()
  const t = (inputs.type || '').toLowerCase()

  if (family === 'fridge-fullsize') {
    if (inputs.features?.hasIceMaker === true) {
      out.push(
        withSchemaAnswer({
          slot: 'icemaker',
          question: `Does ${name.ref} have an ice maker?`,
          answer: `Yes — ${name.ref} includes a built-in ice maker. It needs a water line connection at installation, so plan the hookup (or a shut-off valve behind the fridge) before delivery day.`,
        })
      )
    }
    if (inputs.features?.hasSmartFeatures === true) {
      out.push(
        withSchemaAnswer({
          slot: 'smart',
          question: `Does ${name.ref} have smart features?`,
          answer: `Yes — ${name.ref} has Wi-Fi connectivity, so you can monitor and adjust temperatures and get door-open alerts from the companion app.`,
        })
      )
    }
  }

  if (family === 'chest-freezer' || family === 'upright-freezer') {
    const garage = specFlag(specs, ['garage_ready']) || /garage[- ]?ready/.test(title)
    if (garage) {
      out.push(
        withSchemaAnswer({
          slot: 'garage',
          question: `Can ${name.ref} go in a garage?`,
          answer: `Yes — ${name.ref} is garage-ready, built to hold safe freezing temperatures in unheated spaces through both winter cold and summer heat.`,
        })
      )
    }
    const frostFree = specFlag(specs, ['frost_free']) || /frost[- ]?free/.test(title)
    const manualDefrost = /manual[- ]defrost/.test(title) || specs?.['defrost_type'] === 'manual'
    if (frostFree) {
      out.push(
        withSchemaAnswer({
          slot: 'defrost',
          question: `Is ${name.ref} frost-free?`,
          answer: `Yes — ${name.ref} defrosts automatically, so you never have to empty it to chip out ice. Frost-free models use slightly more energy than manual-defrost ones in exchange for zero maintenance.`,
        })
      )
    } else if (manualDefrost) {
      out.push(
        withSchemaAnswer({
          slot: 'defrost',
          question: `Is ${name.ref} frost-free?`,
          answer: `No — ${name.ref} uses manual defrost, which typically means lower energy use and steadier temperatures, at the cost of defrosting it yourself about once or twice a year when frost builds past a quarter inch.`,
        })
      )
    }
  }

  if (family === 'wine-beverage') {
    const bottles = specNum(specs, ['bottle_capacity', 'bottles'])
    if (bottles != null && !t.includes('beverage')) {
      out.push(
        withSchemaAnswer({
          slot: 'bottles',
          question: `How many bottles does ${name.ref} hold?`,
          answer: `${name.Ref} holds ${Math.round(bottles)} standard Bordeaux-size bottles. Larger Burgundy or Champagne formats take more shelf room, so a mixed collection will fit slightly fewer.`,
        })
      )
    }
    const cans = specNum(specs, ['can_capacity', 'cans'])
    if (cans != null) {
      out.push(
        withSchemaAnswer({
          slot: 'cans',
          question: `How many cans does ${name.ref} hold?`,
          answer: `${name.Ref} holds ${Math.round(cans)} standard 12-oz cans. Taller bottles and slim cans change the count, so treat that as the like-for-like maximum.`,
        })
      )
    }
    const dualZone = specFlag(specs, ['dual_zone']) || /dual[- ]zone/.test(title)
    if (dualZone && !t.includes('beverage')) {
      out.push(
        withSchemaAnswer({
          slot: 'zones',
          question: `Is ${name.ref} single or dual zone?`,
          answer: `${name.Ref} is dual zone — two independently controlled temperature areas, so you can hold reds at 55–65°F and whites at 45–50°F at the same time.`,
        })
      )
    }
  }

  if (family === 'kegerator') {
    const kegMatches = Array.from(
      new Set(
        (inputs.title || '').match(/\b(sixth|sixtel|slim quarter|quarter|half|full[- ]?size|ball[- ]?lock|corny)\b[- ]?(barrel|keg)?/gi) || []
      )
    )
    if (kegMatches.length > 0) {
      out.push(
        withSchemaAnswer({
          slot: 'kegs',
          question: `What keg sizes fit ${name.ref}?`,
          answer: `Per the manufacturer's listing, ${name.ref} accommodates ${kegMatches.map((k) => k.toLowerCase().trim()).join(', ')} configurations. Measure your preferred keg's height with the coupler attached before ordering.`,
        })
      )
    }
  }

  if (family === 'ice-maker') {
    const lbsDay = specNum(specs, ['ice_lbs_per_day', 'daily_ice_production_lbs'])
    if (lbsDay != null) {
      out.push(
        withSchemaAnswer({
          slot: 'output',
          question: `How much ice does ${name.ref} make per day?`,
          answer: `${name.Ref} produces up to ${Math.round(lbsDay)} lbs of ice per 24 hours. Ambient temperature and incoming water temperature affect real-world output, so expect a bit less in hot rooms.`,
        })
      )
    }
  }

  if (family === 'compact' && fitOk && normHeight != null && normHeight <= 34) {
    out.push(
      withSchemaAnswer({
        slot: 'underdesk',
        question: `Will ${name.ref} fit under a desk or counter?`,
        answer: `At ${fmtIn(normHeight)} inches tall, ${name.ref} fits under standard 32–34 inch counters and most desks. Leave a couple of inches above and behind it for ventilation so the compressor can breathe.`,
      })
    )
  }

  return out
}

// ── entry point ──────────────────────────────────────────────────────────────
export function buildSpecFaqs(inputs: SpecFaqInputs): SpecFaq[] {
  const family = applianceFamily(inputs.type, inputs.title)
  if (family === 'other') return []

  const name = displayName(inputs, family)

  const fit = fitFaq(inputs, family, name)
  const raw = { width_in: inputs.width_in ?? null, height_in: inputs.height_in ?? null, depth_in: inputs.depth_in ?? null }
  const norm = normalizeDimensions(raw, inputs.type ?? undefined)

  const universal = [
    fit,
    capacityFaq(inputs, family, name),
    energyFaq(inputs, name),
    noiseFaq(inputs, family, name),
  ].filter((f): f is SpecFaq => f !== null)

  const typeSpecific = typeSpecificFaqs(inputs, family, name, fit !== null, norm.heightInches ?? null).slice(0, 2)

  return [...universal, ...typeSpecific].slice(0, 6)
}
